import { useState, useEffect } from 'react'
import { isValidMgrs, mgrsToDD, formatMgrs, getMgrsPrecision } from '../../utils/geo/mgrs'
import { formatDDPair, formatDMSPair, ddToDms } from '../../utils/geo/formats'
import { useToast } from '../../hooks/useToast'

interface Props {
  value?: string
  onChange?: (mgrs: string) => void
  onPickOnMap?: () => void
  readOnly?: boolean
  placeholder?: string
}

export default function MgrsInput({ value = '', onChange, onPickOnMap, readOnly, placeholder }: Props) {
  const [raw, setRaw] = useState(value)
  const [valid, setValid] = useState<boolean | null>(null)
  const { toast } = useToast()

  useEffect(() => {
    setRaw(value)
  }, [value])

  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    const v = e.target.value.toUpperCase()
    setRaw(v)
    const isV = v === '' ? null : isValidMgrs(v)
    setValid(isV)
    if (onChange) onChange(v)
  }

  function handleBlur() {
    if (valid && raw) {
      const formatted = formatMgrs(raw)
      setRaw(formatted)
      if (onChange) onChange(formatted)
    }
  }

  const dd = valid && raw ? mgrsToDD(raw) : null
  const precision = valid && raw ? getMgrsPrecision(raw) : null

  function copyDD() {
    if (!dd) return
    const text = formatDDPair(dd.lat, dd.lon)
    navigator.clipboard.writeText(text)
    toast({ title: 'Copied DD', description: text })
  }

  function copyDMS() {
    if (!dd) return
    const text = formatDMSPair({
      format: 'dms',
      lat: ddToDms(dd.lat, 'lat'),
      lon: ddToDms(dd.lon, 'lon'),
    })
    navigator.clipboard.writeText(text)
    toast({ title: 'Copied DMS', description: text })
  }

  return (
    <div className="space-y-2">
      <div className="relative flex items-center">
        <input
          type="text"
          value={raw}
          onChange={handleChange}
          onBlur={handleBlur}
          readOnly={readOnly}
          placeholder={placeholder ?? '18S UJ 23395 09512'}
          className={`flex h-9 w-full rounded-md border bg-background px-3 py-1 text-sm font-mono shadow-sm transition-colors
            placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring
            ${valid === false ? 'border-destructive' : valid === true ? 'border-green-500' : 'border-input'}`}
        />
        {valid && precision && (
          <span className="absolute right-2 text-xs text-muted-foreground">{precision}m</span>
        )}
      </div>

      {valid === false && (
        <p className="text-xs text-destructive">Invalid MGRS — expected format: 18S UJ 23395 09512</p>
      )}

      {dd && (
        <div className="rounded-md bg-muted/50 p-2 space-y-1 text-xs font-mono">
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground">DD:</span>
            <div className="flex gap-2">
              <span>{formatDDPair(dd.lat, dd.lon)}</span>
              <button onClick={copyDD} className="text-blue-400 hover:text-blue-300">Copy</button>
              <button onClick={copyDMS} className="text-blue-400 hover:text-blue-300">Copy DMS</button>
            </div>
          </div>
        </div>
      )}

      {onPickOnMap && (
        <button
          type="button"
          onClick={onPickOnMap}
          className="text-xs text-blue-400 hover:text-blue-300 underline underline-offset-2"
        >
          Pick on Map
        </button>
      )}
    </div>
  )
}
